import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { getCustomerPhoneCandidates, customerRequestOptions } from "../../services/customerService";
import { benefitMutation } from "../../services/customerBenefitsService";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  FiAlertCircle,
  FiCalendar,
  FiCheck,
  FiCheckCircle,
  FiClock,
  FiGift,
  FiPhone,
  FiRefreshCw,
  FiSearch,
  FiShield,
  FiTag,
  FiUser,
  FiX,
} from "react-icons/fi";
import "./CheckVoucher.css";

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_POS_API_BASE_URL ||
  "https://doticom.vn:3000"
).replace(/\/+$/, "");

const normalizePhone = (value) =>
  String(value || "")
    .trim()
    .replace(/[\s().-]/g, "");

const getVoucherCode = (voucher) =>
  String(voucher?.MaVoucher || voucher?.MaCK || "").trim();

const getApiMessage = (error, fallbackMessage) =>
  error?.response?.data?.message || error?.message || fallbackMessage;

const formatVietnamDateTimeForApi = (date = new Date()) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Ho_Chi_Minh",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  })
    .formatToParts(date)
    .reduce((result, part) => {
      result[part.type] = part.value;
      return result;
    }, {});

  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}+07:00`;
};

const formatDate = (value, includeTime = false) => {
  if (!value) return "--";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";

  return new Intl.DateTimeFormat("vi-VN", {
    timeZone: "Asia/Ho_Chi_Minh",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    ...(includeTime
      ? {
          hour: "2-digit",
          minute: "2-digit",
        }
      : {}),
  }).format(date);
};

const formatCurrency = (value) =>
  `${new Intl.NumberFormat("vi-VN").format(Number(value) || 0)} đ`;

const getVoucherDiscountText = (voucher) => {
  const discounts = [];

  if (Number(voucher?.ChietKhauPT) > 0) {
    discounts.push(`${Number(voucher.ChietKhauPT)}%`);
  }

  if (Number(voucher?.ChietKhauTM) > 0) {
    discounts.push(formatCurrency(voucher.ChietKhauTM));
  }

  return `Voucher giảm ${discounts.join(" + ") || formatCurrency(0)}`;
};

const isActiveVoucher = (voucher) => {
  const today = formatVietnamDateTimeForApi().slice(0, 10);
  return (voucher?.Act_Voucher === true || voucher?.Act_Voucher === 1)
    && (!voucher.NgayTao || String(voucher.NgayTao).slice(0, 10) <= today)
    && (!voucher.HSD || String(voucher.HSD).slice(0, 10) >= today);
};

const CheckVoucher = () => {
  const [phone, setPhone] = useState("");
  const [searchedPhone, setSearchedPhone] = useState("");
  const [customer, setCustomer] = useState(null);
  const [voucher, setVoucher] = useState(null);
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [usedAt, setUsedAt] = useState("");
  const [saveError, setSaveError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const requestIdRef = useRef(0);
  const savingRef = useRef(false);
  const modalRef = useRef(null);
  const confirmModalRef = useRef(null);

  useEffect(() => {
    if (!isModalOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !savingRef.current) {
        if (isConfirmOpen) {
          setIsConfirmOpen(false);
        } else {
          setIsModalOpen(false);
        }
      }
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);
    (isConfirmOpen ? confirmModalRef : modalRef).current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isConfirmOpen, isModalOpen]);

  const clearResult = () => {
    requestIdRef.current += 1;
    setSearchedPhone("");
    setCustomer(null);
    setVoucher(null);
    setStatus("idle");
    setMessage("");
    setUsedAt("");
    setSaveError("");
    setIsModalOpen(false);
    setIsConfirmOpen(false);
  };

  const handlePhoneChange = (event) => {
    const nextPhone = event.target.value;
    setPhone(nextPhone);

    if (searchedPhone && normalizePhone(nextPhone) !== searchedPhone) {
      clearResult();
    }
  };

  const handleCheckVoucher = async (event) => {
    event.preventDefault();

    const normalizedPhone = normalizePhone(phone);
    if (!/^\+?\d{8,15}$/.test(normalizedPhone)) {
      clearResult();
      setStatus("validation-error");
      setMessage("Vui lòng nhập số điện thoại hợp lệ từ 8 đến 15 chữ số.");
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setSearchedPhone(normalizedPhone);
    setCustomer(null);
    setVoucher(null);
    setUsedAt("");
    setSaveError("");
    setMessage("");
    setIsModalOpen(false);
    setIsConfirmOpen(false);
    setStatus("checking");

    let foundCustomer;
    let customerPhoneForUsage = normalizedPhone;
    let customerNotFoundMessage = "";
    try {
      const phoneCandidates = getCustomerPhoneCandidates(normalizedPhone);

      for (const phoneCandidate of phoneCandidates) {
        let customerResponse;
        try {
          customerResponse = await axios.get(
            `${API_BASE_URL}/customers?phone=${encodeURIComponent(phoneCandidate)}`,
            customerRequestOptions(),
          );
        } catch (error) {
          if (requestId !== requestIdRef.current) return;

          if (error?.response?.status === 404) {
            customerNotFoundMessage =
              error.response.data?.message || customerNotFoundMessage;
            continue;
          }

          throw error;
        }

        if (requestId !== requestIdRef.current) return;

        foundCustomer = Array.isArray(customerResponse.data)
          ? customerResponse.data[0]
          : null;

        if (foundCustomer) {
          customerPhoneForUsage =
            normalizePhone(foundCustomer.SDT || phoneCandidate) || phoneCandidate;
          break;
        }

        customerNotFoundMessage =
          customerResponse.data?.message || customerNotFoundMessage;
      }

      if (!foundCustomer) {
        setStatus("not-member");
        setMessage(
          customerNotFoundMessage ||
            "Không tìm thấy hội viên với số điện thoại này.",
        );
        return;
      }

      setCustomer({
        ...foundCustomer,
        MaKH: String(foundCustomer.MaKH || "").trim(),
        SDT: String(foundCustomer.SDT || customerPhoneForUsage).trim(),
        Ten: String(foundCustomer.Ten || "Hội viên").trim(),
        Membership: String(foundCustomer.Membership || "").trim(),
      });
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      setStatus("error");
      setMessage(
        getApiMessage(error, "Không thể kiểm tra thông tin hội viên lúc này."),
      );
      return;
    }

    let currentVoucher;
    try {
      const voucherResponse = await axios.get(
        `${API_BASE_URL}/check-ctkm-voucher`,
      );
      if (requestId !== requestIdRef.current) return;

      const vouchers = Array.isArray(voucherResponse.data)
        ? voucherResponse.data
        : [];
      currentVoucher = vouchers.find(isActiveVoucher);

      if (!currentVoucher || !getVoucherCode(currentVoucher)) {
        setStatus("no-voucher");
        setMessage("Hiện chưa có voucher nào đang được áp dụng.");
        setIsModalOpen(true);
        return;
      }

      currentVoucher = {
        ...currentVoucher,
        MaVoucher: getVoucherCode(currentVoucher),
      };
      setVoucher(currentVoucher);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      setStatus("error");
      setMessage(
        getApiMessage(error, "Không thể lấy thông tin voucher hiện tại."),
      );
      setIsModalOpen(true);
      return;
    }

    try {
      const usageResponse = await axios.get(`${API_BASE_URL}/api/customers/vouchers`, customerRequestOptions({ params: { phone: customerPhoneForUsage, code: getVoucherCode(currentVoucher) } }));
      if (requestId !== requestIdRef.current) return;

      const allocation = usageResponse.data?.data?.[0];
      if (allocation?.status === "used") {
        setStatus("used");
        setUsedAt(allocation.NgaySuDung || "");
        setMessage("Hội viên đã sử dụng voucher hiện tại.");
        setIsModalOpen(true);
        return;
      }
      if (!allocation || allocation.status !== "available") throw new Error("Voucher không thể sử dụng cho hội viên này.");

      setStatus("eligible");
      setMessage("Hội viên đủ điều kiện sử dụng voucher.");
      setIsModalOpen(true);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      setStatus("error");
      setMessage(
        getApiMessage(error, "Không thể kiểm tra lịch sử sử dụng voucher."),
      );
      setIsModalOpen(true);
    }
  };

  const handleUseVoucher = async () => {
    if (status !== "eligible" || !customer || !voucher || savingRef.current) {
      return;
    }

    const customerCode = String(
      customer.MaKH || customer.SDT || searchedPhone,
    ).trim();
    const voucherCode = getVoucherCode(voucher);

    if (!customerCode || !voucherCode) {
      setSaveError("Thiếu mã khách hàng hoặc mã voucher để lưu sử dụng.");
      return;
    }

    savingRef.current = true;
    setIsSaving(true);
    setSaveError("");
    const usageDate = formatVietnamDateTimeForApi();

    try {
      await benefitMutation("post", "/api/customers/vouchers/use", { phone: customer.SDT, MaCK: voucherCode, reason: "Sử dụng chương trình Voucher hội viên" });

      setUsedAt(usageDate);
      setStatus("saved");
      setMessage(
        "Đã ghi nhận sử dụng voucher thành công.",
      );
    } catch (error) {
      setSaveError(
        getApiMessage(error, "Không thể lưu sử dụng voucher. Vui lòng thử lại."),
      );
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  const handleCheckAnother = () => {
    clearResult();
    setPhone("");
  };

  const currentStep =
    status === "saved"
      ? 4
      : status === "eligible"
        ? 3
        : status === "checking" || customer
          ? 2
          : 1;

  const alertClass = {
    "validation-error": "danger",
    "not-member": "danger",
    error: "danger",
    "no-voucher": "info",
    used: "warning",
    saved: "success",
  }[status];

  const showVoucherCard =
    voucher && ["eligible", "used", "saved"].includes(status);
  const isVoucherUsed = ["used", "saved"].includes(status);
  const isVoucherCodeVisible = status === "saved";

  return (
    <div className="check-voucher-page">
      <main className="container py-4 py-md-5">
        <div className="check-voucher-shell mx-auto">
          <header className="check-voucher-header text-center">
            <div className="check-voucher-logo" aria-hidden="true">
              <FiGift />
            </div>
            <p className="check-voucher-eyebrow mb-2">ƯU ĐÃI HỘI VIÊN</p>
            <h1 className="check-voucher-title">Kiểm tra voucher</h1>
            <p className="check-voucher-subtitle mb-0">
              Nhập số điện thoại để kiểm tra và ghi nhận ưu đãi nhanh chóng.
            </p>
          </header>

          <section className="check-voucher-card card border-0">
            <div className="card-body p-3 p-md-5">
              <div className="check-voucher-steps" aria-label="Quy trình sử dụng voucher">
                {[
                  "Nhập SĐT",
                  "Kiểm tra ưu đãi",
                  "Sử dụng voucher",
                ].map((label, index) => {
                  const stepNumber = index + 1;
                  const isComplete = currentStep > stepNumber;
                  const isActive = currentStep === stepNumber;

                  return (
                    <React.Fragment key={label}>
                      {index > 0 && (
                        <span
                          className={`check-voucher-step-line ${
                            currentStep > stepNumber - 1 ? "is-complete" : ""
                          }`}
                          aria-hidden="true"
                        />
                      )}
                      <div
                        className={`check-voucher-step ${
                          isComplete ? "is-complete" : ""
                        } ${isActive ? "is-active" : ""}`}
                      >
                        <span className="check-voucher-step-number">
                          {isComplete ? <FiCheck /> : stepNumber}
                        </span>
                        <span className="check-voucher-step-label">{label}</span>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>

              <form className="check-voucher-form" onSubmit={handleCheckVoucher}>
                <label htmlFor="voucher-phone" className="form-label fw-semibold">
                  Số điện thoại khách hàng
                </label>
                <div className="check-voucher-search-row">
                  <div className="check-voucher-input-wrap flex-grow-1">
                    <FiPhone aria-hidden="true" />
                    <input
                      id="voucher-phone"
                      type="tel"
                      className="form-control"
                      value={phone}
                      onChange={handlePhoneChange}
                      placeholder="Ví dụ: 0901 000 001"
                      autoComplete="tel"
                      inputMode="tel"
                      autoFocus
                      disabled={status === "checking" || isSaving}
                      aria-describedby="voucher-phone-help"
                    />
                  </div>
                  <button
                    type="submit"
                    className="btn check-voucher-search-btn"
                    disabled={status === "checking" || isSaving}
                  >
                    {status === "checking" ? (
                      <>
                        <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                        Đang kiểm tra
                      </>
                    ) : (
                      <>
                        <FiSearch aria-hidden="true" />
                        Kiểm tra
                      </>
                    )}
                  </button>
                </div>
                <div id="voucher-phone-help" className="form-text">
                  Hệ thống chỉ hiển thị voucher khi khách hàng là hội viên và chưa sử dụng.
                </div>
              </form>

              <div className="check-voucher-result" aria-live="polite">
                {status === "checking" && (
                  <div className="check-voucher-loading text-center">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Đang kiểm tra...</span>
                    </div>
                    <p className="mb-0 mt-3 fw-semibold">
                      Đang kiểm tra thông tin hội viên và voucher...
                    </p>
                  </div>
                )}

                {alertClass && message && !customer && (
                  <div
                    className={`alert alert-${alertClass} check-voucher-alert d-flex gap-3`}
                    role="alert"
                  >
                    <span className="check-voucher-alert-icon" aria-hidden="true">
                      {status === "saved" ? <FiCheckCircle /> : <FiAlertCircle />}
                    </span>
                    <div>
                      <div className="fw-semibold">{message}</div>
                    </div>
                  </div>
                )}

                {customer && status !== "checking" && !isModalOpen && (
                  <div className="text-center">
                    <button
                      type="button"
                      className="btn btn-outline-secondary check-voucher-reset-btn"
                      onClick={() => setIsModalOpen(true)}
                    >
                      <FiGift aria-hidden="true" />
                      Xem kết quả voucher
                    </button>
                  </div>
                )}
              </div>
            </div>
          </section>

          <footer className="check-voucher-footer">
            <span><FiShield aria-hidden="true" /> Thông tin được kiểm tra an toàn</span>
            <span><FiClock aria-hidden="true" /> Ghi nhận theo giờ Việt Nam</span>
          </footer>
        </div>
      </main>

      {isModalOpen && customer && (
        <div className="check-voucher-modal-backdrop">
          <section
            ref={modalRef}
            className="check-voucher-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="check-voucher-modal-title"
            tabIndex="-1"
          >
            <div className="check-voucher-modal-header">
              <div>
                <p className="check-voucher-eyebrow mb-1">TRA CỨU VOUCHER</p>
                <h2 id="check-voucher-modal-title" className="h4 mb-0">
                  Thông tin khách hàng và voucher
                </h2>
              </div>
              <button
                type="button"
                className="check-voucher-modal-close"
                onClick={() => setIsModalOpen(false)}
                disabled={isSaving}
                aria-label="Đóng"
              >
                <FiX aria-hidden="true" />
              </button>
            </div>

            <div className="check-voucher-modal-body">
              <section className="check-voucher-customer" aria-label="Thông tin hội viên">
                <div className="check-voucher-customer-icon" aria-hidden="true">
                  <FiUser />
                </div>
                <div className="flex-grow-1">
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                    <h3 className="h5 mb-0">{customer.Ten || "Hội viên"}</h3>
                    <span className="badge rounded-pill check-voucher-member-badge">
                      {customer.Membership || "Hội viên"}
                    </span>
                  </div>
                  <p className="mb-0 text-secondary">{customer.SDT || searchedPhone}</p>
                </div>
                <FiCheckCircle className="check-voucher-customer-check" aria-label="Đã xác minh" />
              </section>

              {alertClass && message && (
                <div
                  className={`alert alert-${alertClass} check-voucher-alert d-flex gap-3`}
                  role="alert"
                >
                  <span className="check-voucher-alert-icon" aria-hidden="true">
                    {status === "saved" ? <FiCheckCircle /> : <FiAlertCircle />}
                  </span>
                  <div>
                    <div className="fw-semibold">{message}</div>
                    {status === "used" && usedAt && (
                      <div className="small mt-1">
                        Đã dùng lúc {formatDate(usedAt, true)}
                      </div>
                    )}
                    {status === "saved" && usedAt && (
                      <div className="small mt-1">
                        Ghi nhận lúc {formatDate(usedAt, true)}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {showVoucherCard && (
                <section
                  className={`check-voucher-ticket ${
                    isVoucherUsed ? "is-used" : ""
                  }`}
                  aria-label="Voucher hiện tại"
                >
                  <div className="check-voucher-ticket-main">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <span className="check-voucher-ticket-label">
                        <FiTag aria-hidden="true" /> Voucher hiện tại
                      </span>
                      {isVoucherUsed && (
                        <span className="badge rounded-pill check-voucher-used-badge">
                          Đã sử dụng
                        </span>
                      )}
                    </div>
                    {isVoucherCodeVisible ? (
                      <div className="check-voucher-code">{getVoucherCode(voucher)}</div>
                    ) : status === "used" ? (
                      <div className="check-voucher-code-hidden">
                        <FiGift aria-hidden="true" />
                        <span>{getVoucherDiscountText(voucher)}</span>
                      </div>
                    ) : (
                      <div className="check-voucher-code-hidden" aria-label="Mã voucher đang được ẩn">
                        <FiShield aria-hidden="true" />
                        <span>
                          Voucher giảm 20.000VND
                          <br />
                          Tri ân khách hàng cũ
                        </span>
                      </div>
                    )}
                    <div className="check-voucher-ticket-meta">
                      <span>
                        <FiCalendar aria-hidden="true" /> Hạn dùng: {formatDate(voucher.HSD)}
                      </span>
                      {Number(voucher.ChietKhauPT) > 0 && (
                        <span>
                          <FiGift aria-hidden="true" /> Giảm {voucher.ChietKhauPT}%
                        </span>
                      )}
                      {Number(voucher.ChietKhauTM) > 0 && (
                        <span>
                          <FiGift aria-hidden="true" /> Giảm {formatCurrency(voucher.ChietKhauTM)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="check-voucher-ticket-side" aria-hidden="true">
                    <FiGift />
                  </div>
                </section>
              )}

              {status === "eligible" && (
                <div className="check-voucher-action-area text-center">
                  <p className="mb-3 text-secondary">
                    Vui lòng nhấn "Sử dụng voucher" và đưa mã voucher cho thu ngân.
                  </p>
                  {saveError && (
                    <div className="alert alert-danger check-voucher-alert text-start" role="alert">
                      {saveError}
                    </div>
                  )}
                  <button
                    type="button"
                    className="btn btn-lg check-voucher-use-btn"
                    onClick={() => setIsConfirmOpen(true)}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                        Đang ghi nhận
                      </>
                    ) : (
                      <>
                        <FiCheckCircle aria-hidden="true" />
                        Sử dụng voucher
                      </>
                    )}
                  </button>
                </div>
              )}

              {status === "saved" && (
                <div className="text-center mt-4">
                  <button
                    type="button"
                    className="btn btn-outline-secondary check-voucher-reset-btn"
                    onClick={handleCheckAnother}
                  >
                    <FiRefreshCw aria-hidden="true" />
                    Kiểm tra khách hàng khác
                  </button>
                </div>
              )}
            </div>
          </section>
        </div>
      )}

      {isModalOpen && isConfirmOpen && customer && voucher && (
        <div className="check-voucher-modal-backdrop check-voucher-confirm-backdrop">
          <section
            ref={confirmModalRef}
            className="check-voucher-confirm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="check-voucher-confirm-title"
            tabIndex="-1"
          >
            <div className="check-voucher-confirm-icon" aria-hidden="true">
              <FiAlertCircle />
            </div>
            <h2 id="check-voucher-confirm-title" className="h4">
              Bạn có chắc chắn muốn sử dụng voucher này?
            </h2>
            <p className="text-secondary mb-0">
              Voucher chỉ có thể sử dụng một lần duy nhất. Sau khi xác nhận, vui lòng đưa mã voucher cho nhân viên.
            </p>
            <div className="check-voucher-confirm-actions">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setIsConfirmOpen(false)}
              >
                Hủy
              </button>
              <button
                type="button"
                className="btn check-voucher-confirm-btn"
                onClick={() => {
                  setIsConfirmOpen(false);
                  handleUseVoucher();
                }}
              >
                <FiCheckCircle aria-hidden="true" />
                Xác nhận sử dụng
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export { formatVietnamDateTimeForApi, normalizePhone };
export default CheckVoucher;
